import React, { useState } from 'react';
import { DashboardLayout } from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';
import { authApi } from '../../lib/api';
import { toast } from 'sonner';
import { Mail, KeyRound, Loader2, Save } from 'lucide-react';

export default function SettingsPage() {
  const { user, updateUser } = useAuth();

  // --- E-posta değiştir state ---
  const [newEmail, setNewEmail] = useState('');
  const [confirmNewEmail, setConfirmNewEmail] = useState('');
  const [emailCurrentPassword, setEmailCurrentPassword] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  // --- Şifre değiştir state ---
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    const trimmed = newEmail.trim().toLowerCase();
    const trimmedConfirm = confirmNewEmail.trim().toLowerCase();

    if (!trimmed) {
      toast.error('Yeni e-posta adresi giriniz');
      return;
    }
    if (trimmed !== trimmedConfirm) {
      toast.error('Yeni e-posta adresleri eşleşmiyor');
      return;
    }
    if (trimmed === (user?.email || '').toLowerCase()) {
      toast.error('Yeni e-posta mevcut e-posta ile aynı');
      return;
    }
    if (!emailCurrentPassword) {
      toast.error('Doğrulama için mevcut şifrenizi girin');
      return;
    }

    setSavingEmail(true);
    try {
      const result = await authApi.changeEmail({
        new_email: trimmed,
        current_password: emailCurrentPassword,
      });
      updateUser({ email: result.email });
      toast.success('E-posta adresi güncellendi');
      setNewEmail('');
      setConfirmNewEmail('');
      setEmailCurrentPassword('');
    } catch (err) {
      const msg = err?.message || 'E-posta değiştirilemedi';
      toast.error(msg);
    } finally {
      setSavingEmail(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!currentPassword) {
      toast.error('Mevcut şifrenizi girin');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Yeni şifre en az 8 karakter olmalı');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }
    if (newPassword === currentPassword) {
      toast.error('Yeni şifre eskisiyle aynı olamaz');
      return;
    }

    setSavingPassword(true);
    try {
      await authApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Şifre güncellendi');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      const msg = err?.message || 'Şifre değiştirilemedi';
      toast.error(msg);
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <DashboardLayout title="Ayarlar" subtitle="Hesap ayarlarınızı yönetin">
      <div className="max-w-2xl mx-auto space-y-6" data-testid="superadmin-settings-page">
        {/* E-posta Değiştir */}
        <Card className="border-0 shadow-lg overflow-hidden">
          <CardHeader className="py-4 px-6 bg-gradient-to-r from-[#5A67D8] to-[#4C51BF]">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Mail className="w-4 h-4" />
              E-posta Değiştir
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form className="space-y-4" onSubmit={handleChangeEmail}>
              <div>
                <Label htmlFor="current-email">Mevcut E-posta</Label>
                <Input
                  id="current-email"
                  type="email"
                  value={user?.email || ''}
                  readOnly
                  disabled
                  className="bg-slate-50 mt-1.5"
                  data-testid="settings-current-email"
                />
              </div>

              <div>
                <Label htmlFor="new-email">Yeni E-posta</Label>
                <Input
                  id="new-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="yeni@ornek.com"
                  className="mt-1.5"
                  data-testid="settings-new-email-input"
                />
              </div>

              <div>
                <Label htmlFor="confirm-new-email">Yeni E-posta (Tekrar)</Label>
                <Input
                  id="confirm-new-email"
                  type="email"
                  value={confirmNewEmail}
                  onChange={(e) => setConfirmNewEmail(e.target.value)}
                  placeholder="yeni@ornek.com"
                  className="mt-1.5"
                  data-testid="settings-confirm-new-email-input"
                />
              </div>

              <div>
                <Label htmlFor="email-current-password">Mevcut Şifre (doğrulama için)</Label>
                <Input
                  id="email-current-password"
                  type="password"
                  value={emailCurrentPassword}
                  onChange={(e) => setEmailCurrentPassword(e.target.value)}
                  placeholder="Şifrenizi girin"
                  className="mt-1.5"
                  autoComplete="current-password"
                  data-testid="settings-email-current-password-input"
                />
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  disabled={savingEmail}
                  className="bg-[#5A67D8] hover:bg-[#4C51BF] text-white"
                  data-testid="settings-change-email-submit-btn"
                >
                  {savingEmail ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Kaydediliyor...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      E-postayı Değiştir
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Şifre Değiştir */}
        <Card className="border-0 shadow-lg overflow-hidden">
          <CardHeader className="py-4 px-6 bg-gradient-to-r from-[#5A67D8] to-[#4C51BF]">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <KeyRound className="w-4 h-4" />
              Şifre Değiştir
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form className="space-y-4" onSubmit={handleChangePassword}>
              <div>
                <Label htmlFor="current-password">Mevcut Şifre</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Mevcut şifreniz"
                  className="mt-1.5"
                  autoComplete="current-password"
                  data-testid="settings-current-password-input"
                />
              </div>

              <div>
                <Label htmlFor="new-password">Yeni Şifre</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="En az 8 karakter"
                  className="mt-1.5"
                  autoComplete="new-password"
                  data-testid="settings-new-password-input"
                />
              </div>

              <div>
                <Label htmlFor="confirm-new-password">Yeni Şifre (Tekrar)</Label>
                <Input
                  id="confirm-new-password"
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  placeholder="Yeni şifreyi tekrar girin"
                  className="mt-1.5"
                  autoComplete="new-password"
                  data-testid="settings-confirm-new-password-input"
                />
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  disabled={savingPassword}
                  className="bg-[#5A67D8] hover:bg-[#4C51BF] text-white"
                  data-testid="settings-change-password-submit-btn"
                >
                  {savingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Kaydediliyor...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Şifreyi Değiştir
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
